import { AuthService } from '@omni/kit/auth';

import { validateToken } from '../../../auth/Service/validateToken';
import { SUBSPLASH_AUTH_PROVIDER_ID } from '../../../constants/identifiers';
import BaseService, { IHttpResponse, IUri } from '../../BaseService';
import { prepareUrl } from '../BuildUrls';
import { IChatSession } from '../Types';

const RESOURCE = 'chat-sessions';

interface ICreateSession extends Omit<IUri, 'resource'> {
  appKey: string;
  attempt?: number;
  attemptLimit?: number;
  source?: string;
}

export default async function createChatSession({
  appKey,
  attempt = 1,
  attemptLimit = 4,
  source,
  ...buildUrlProps
}: ICreateSession): Promise<IChatSession> {
  // DISP-5859: if token is expired, refresh it before making the request
  if (
    !validateToken(
      AuthService.subsplashAccessToken,
      undefined,
      appKey,
      undefined
    )
  ) {
    await AuthService.refreshAccessToken(SUBSPLASH_AUTH_PROVIDER_ID, source);
    AuthService.notifyAuthorizationChanged();
  }

  const token = AuthService.subsplashAccessTokenWithBearer;

  if (!token) {
    throw new Error('No access token provided');
  }

  const url = prepareUrl({
    ...buildUrlProps,
    resource: RESOURCE,
  });

  const headers: Record<string, string> = {
    'sap-active-appkey': appKey,
  };

  const response = await BaseService.Post({
    url,
    appKey,
    headers,
    token,
  });

  const ok = response.status === 200 || response.status === 201;
  const isRetryable =
    attempt <= attemptLimit &&
    (!response.status || response.status === 429 || response.status >= 500);

  if (!ok && isRetryable) {
    // use a slight delay before next attempt
    await new Promise((resolve) => setTimeout(resolve, attempt * 1000));

    return createChatSession({
      appKey,
      attempt: attempt + 1,
      attemptLimit,
      source,
      ...buildUrlProps,
    });
  }

  return handleResponse(response);
}

function handleResponse(response: IHttpResponse): Promise<IChatSession> {
  if (response?.status && response?.status >= 200 && response?.status < 400) {
    return Promise.resolve(response.body);
  }

  return Promise.reject(`Failed to create a chat-session: ${response?.status}`);
}
